<template>
  <div
    class="number-input"
    :class="{
      'number-input--inline': inline,
      'number-input--center': center,
      'number-input--controls': controls,
      [`number-input--${size}`]: size
    }"
    v-on="listeners"
  >
    <a
      v-if="controls"
      class="number-input__button number-input__button--minus"
      :disabled="disabled || readonly || !decreasable"
      @click="decrease"
    >
      <img
        v-if="decreasable"
        class="number-input__img number-input__button--minus"
        :src="require(`@/assets/images/letters/number-input/minus-circle.svg`)"
      />
      <img
        v-else
        class="number-input__img number-input__button--minus"
        :src="
          require(`@/assets/images/letters/number-input/minus-circle-disabled.svg`)
        "
      />
    </a>
    <input
      ref="input"
      class="number-input__input"
      type="number"
      :name="name"
      :value="currentValue"
      :min="min"
      :max="max"
      :step="step"
      :readonly="readonly || !inputtable"
      :disabled="disabled || (!decreasable && !increasable)"
      :placeholder="placeholder"
      autocomplete="off"
      @change="change"
      @paste="paste"
    />
    <a
      v-if="controls"
      class="number-input__button number-input__button--plus"
      :disabled="disabled || readonly || !increasable"
      @click="increase"
    >
      <img
        v-if="increasable"
        class="number-input__img number-input__button--plus"
        :src="require(`@/assets/images/letters/number-input/plus-circle.svg`)"
      />
      <img
        v-else
        class="number-input__img number-input__button--plus"
        :src="
          require(`@/assets/images/letters/number-input/plus-circle-disabled.svg`)
        "
      />
    </a>
  </div>
</template>

<script>
const isNaN = Number.isNaN || window.isNaN
const REGEXP_NUMBER = /^-?(?:\d+|\d+\.\d+|\.\d+)(?:[eE][-+]?\d+)?$/
const REGEXP_DECIMALS = /\.\d*(?:0|9){10}\d*$/
const normalizeDecimalNumber = (value, times = 100000000000) =>
  REGEXP_DECIMALS.test(value) ? Math.round(value * times) / times : value
export default {
  name: 'NumberInput',
  model: {
    event: 'change'
  },
  props: {
    center: Boolean,
    controls: Boolean,
    disabled: Boolean,
    inputtable: {
      type: Boolean,
      default: true
    },
    inline: Boolean,
    max: {
      type: Number,
      default: Infinity
    },
    min: {
      type: Number,
      default: -Infinity
    },
    name: {
      type: String,
      default: undefined
    },
    placeholder: {
      type: String,
      default: undefined
    },
    readonly: Boolean,
    rounded: Boolean,
    size: {
      type: String,
      default: undefined
    },
    step: {
      type: Number,
      default: 1
    },
    value: {
      type: Number,
      default: NaN
    }
  },
  data() {
    return {
      currentValue: NaN
    }
  },
  computed: {
    /**
     * Indicate if the value is increasable.
     * @returns {boolean} Return `true` if it is decreasable, else `false`.
     */
    increasable() {
      const num = this.currentValue
      return isNaN(num) || num < this.max
    },
    /**
     * Indicate if the value is decreasable.
     * @returns {boolean} Return `true` if it is decreasable, else `false`.
     */
    decreasable() {
      const num = this.currentValue
      return isNaN(num) || num > this.min
    },
    /**
     * Filter listeners
     * @returns {Object} Return filtered listeners.
     */
    listeners() {
      const listeners = { ...this.$listeners }
      delete listeners.change
      return listeners
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue, oldValue) {
        if (
          // Avoid triggering change event when created
          !(isNaN(newValue) && typeof oldValue === 'undefined') &&
          // Avoid infinite loop
          newValue !== this.currentValue
        ) {
          this.setValue(newValue)
        }
      }
    },
    max: {
      immediate: true,
      handler(newValue) {
        // set current value to max if current value is greater than max
        if (this.currentValue > newValue) {
          this.setValue(newValue)
        }
      }
    }
  },
  methods: {
    /**
     * Change event handler.
     * @param {string} value - The new value.
     */
    change(event) {
      this.setValue(Math.min(this.max, Math.max(this.min, event.target.value)))
    },
    /**
     * Paste event handler.
     * @param {Event} event - Event object.
     */
    paste(event) {
      const clipboardData = event.clipboardData || window.clipboardData
      if (clipboardData && !REGEXP_NUMBER.test(clipboardData.getData('text'))) {
        event.preventDefault()
      }
    },
    /**
     * Decrease the value.
     */
    decrease() {
      if (this.decreasable) {
        let { currentValue } = this
        if (isNaN(currentValue)) {
          currentValue = 0
        }
        this.setValue(
          Math.min(
            this.max,
            Math.max(this.min, normalizeDecimalNumber(currentValue - this.step))
          )
        )
      }
    },
    /**
     * Increase the value.
     */
    increase() {
      if (this.increasable) {
        let { currentValue } = this
        if (isNaN(currentValue)) {
          currentValue = 0
        }
        this.setValue(
          Math.min(
            this.max,
            Math.max(this.min, normalizeDecimalNumber(currentValue + this.step))
          )
        )
      }
    },
    /**
     * Set new value and dispatch change event.
     * @param {number} value - The new value to set.
     */
    setValue(value) {
      const oldValue = this.currentValue
      let newValue = this.rounded ? Math.round(value) : value
      if (this.min <= this.max) {
        newValue = Math.min(this.max, Math.max(this.min, newValue))
      }
      this.currentValue = newValue
      if (newValue === oldValue) {
        // Force to override the number in the input box (#13).
        this.$refs.input.value = newValue
      }
      this.$emit('change', newValue, oldValue)
    }
  }
}
</script>

<style lang="scss" scoped>
.number-input {
  display: flex;
  margin-top: auto;
  align-items: center;
  justify-content: center;
  font-size: 0;
  max-width: 100%;
  overflow: hidden;
  &__button {
    &:hover {
      &::before,
      &::after {
        fill: #0074d9;
      }
    }
    &:disabled {
      opacity: 0.65;
      &::before,
      &::after {
        fill: #ddd;
      }
    }
  }
  &__image {
    &:hover {
      fill: #ddd;
    }
  }
  &__input {
    flex-grow: 1;
    margin: 0 1.6rem;
    background-color: #fff;
    border: 1px solid #ddd;
    line-height: 1.5;
    max-width: 50%;
    min-height: 1.5rem;
    transition: border-color 0.15s;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }
    &:focus {
      border-color: #0074d9;
      outline: none;
    }
    &:disabled,
    &[readonly] {
      background-color: #f8f8f8;
    }
  }
  &--inline {
    display: inline-block;
    & > input {
      display: inline-block;
      width: 100%;
    }
  }
  &--center {
    & > input {
      text-align: center;
    }
  }
  &--controls {
    & > input {
      padding-left: 3.375rem;
      padding-right: 3.375rem;
    }
  }
  &--custom {
    & > input {
      font-size: 2.4rem;
      padding: 0.5rem 0.5rem;
      color: $blue700;
    }
    &.number-input--inline > input {
      width: 100%;
    }
    &.number-input--controls > input {
      padding: 1rem 0;
    }
  }
}
</style>
